import React, { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'

// Material UI Components
import Grid from '@material-ui/core/Grid'
import TextField from '@material-ui/core/TextField'
import Select from '@material-ui/core/Select'
import MenuItem from '@material-ui/core/MenuItem'
import FormControl from '@material-ui/core/FormControl'
import InputLabel from '@material-ui/core/InputLabel'
import Switch from '@material-ui/core/Switch'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import InputAdornment from '@material-ui/core/InputAdornment'

// Components
import Button from 'components/CustomButtons/Button'

// External
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'

// Style
import { makeStyles, withStyles } from '@material-ui/core/styles'
import styles from 'assets/jss/material-dashboard-pro-react/views/Apps/invoiceStyle.js'
import { InvoiceProcess } from 'utils/Constants'

const useStyles = makeStyles(styles)

const ColorSwitch = withStyles({
  switchBase: {
    color: '#fafafa',
    '&$checked': {
      color: '#f15f22'
    },
    '&$checked + $track': {
      backgroundColor: '#f15f22'
    }
  },
  checked: {},
  track: {}
})(Switch)

const schema = yup.object().shape({
  name: yup
    .string()
    .min(1)
    .required(),
  description: yup
    .string()
    .min(1)
    .required(),
  enabled: yup.boolean(),
  tolerancePercentage: yup.lazy(val => (val !== '' ? yup.number().min(1) : yup.string())),
  autoReject: yup.boolean(),
  supplierReview: yup.boolean(),
  absoluteTolerance: yup.lazy(val => (val !== '' ? yup.number().min(1) : yup.string())),
  errorMessage: yup
    .string()
    .min(1)
    .required(),
  successMessage: yup
    .string()
    .min(1)
    .required(),
  level: yup
    .string()
    .min(1)
    .required(),
  process: yup
    .string()
    .min(1)
    .required()
})

export default function MasterDataForm(props) {
  const classes = useStyles()
  const [isLoading, setIsLoading] = useState(false)
  const { defaultValues, onSubmitForm } = props

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset
  } = useForm({
    mode: 'all',
    defaultValues: defaultValues,
    resolver: yupResolver(schema),
    shouldUnregister: false
  })

  useEffect(() => {
    if (defaultValues) {
      defaultValues.absoluteTolerance =
        defaultValues.params && defaultValues.params?.absoluteTolerance
          ? defaultValues.params?.absoluteTolerance
          : ''
      defaultValues.tolerancePercentage =
        defaultValues.params && defaultValues.params?.tolerancePercentage
          ? defaultValues.params?.tolerancePercentage * 100
          : ''
      reset(defaultValues)
    }
  }, [defaultValues, reset])

  function isError(field) {
    if (errors[field]) {
      return true
    }
    return false
  }

  function onSubmit(data) {
    setIsLoading(true)
    if (data.absoluteTolerance && data.absoluteTolerance !== '') {
      data.params
        ? (data.params.absoluteTolerance = data.absoluteTolerance)
        : (data.params = { absoluteTolerance: data.absoluteTolerance })
    } else {
      if (data.params) {
        delete data.params.absoluteTolerance
      }
    }

    if (data.tolerancePercentage && data.tolerancePercentage !== '') {
      data.params
        ? (data.params.tolerancePercentage = data.tolerancePercentage / 100)
        : (data.params = {
            tolerancePercentage: data.tolerancePercentage / 100
          })
    } else {
      if (data.params) {
        delete data.params.tolerancePercentage
      }
    }
    if (data.params && Object.keys(data.params).length === 0) {
      delete data.params
    }
    delete data.absoluteTolerance
    delete data.tolerancePercentage

    onSubmitForm(data)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container justifyContent="center" spacing={3}>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <Controller
            render={({ field }) => (
              <TextField
                fullWidth
                variant="outlined"
                className={classes.formField}
                label="Name*"
                error={isError('name')}
                {...field}
              />
            )}
            name="name"
            control={control}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <FormControl variant="outlined" className={classes.formField} error={isError('level')}>
            <InputLabel id="level-label">Level*</InputLabel>
            <Controller
              render={({ field }) => (
                <Select
                  labelId="level-label"
                  label="Level"
                  {...field}
                  inputProps={{ readOnly: true }}
                >
                  <MenuItem disabled>Select Level</MenuItem>
                  <MenuItem value="header">Header</MenuItem>
                  <MenuItem value="lineItems">Line Items</MenuItem>
                </Select>
              )}
              name="level"
              control={control}
            />
          </FormControl>
        </Grid>
        {defaultValues.absoluteTolerance ? (
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <Controller
              render={({ field }) => (
                <TextField
                  fullWidth
                  variant="outlined"
                  className={classes.formField}
                  label="Absolute Tolerance*"
                  error={isError('absoluteTolerance')}
                  {...field}
                />
              )}
              name="absoluteTolerance"
              control={control}
            />
          </Grid>
        ) : null}
        {defaultValues.tolerancePercentage ? (
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <Controller
              render={({ field }) => (
                <TextField
                  fullWidth
                  variant="outlined"
                  className={classes.formField}
                  label="Tolerance Percentage*"
                  error={isError('tolerancePercentage')}
                  {...field}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>
                  }}
                />
              )}
              name="tolerancePercentage"
              control={control}
            />
          </Grid>
        ) : null}
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <Controller
            render={({ field }) => (
              <TextField
                InputProps={{
                  readOnly: true
                }}
                fullWidth
                variant="outlined"
                className={classes.formField}
                label="Description*"
                multiline
                rows="3"
                error={isError('description')}
                {...field}
              />
            )}
            name="description"
            control={control}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <Controller
            render={({ field }) => (
              <TextField
                fullWidth
                variant="outlined"
                className={classes.formField}
                label="Error Message*"
                multiline
                rows="3"
                error={isError('errorMessage')}
                {...field}
              />
            )}
            name="errorMessage"
            control={control}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <Controller
            render={({ field }) => (
              <TextField
                fullWidth
                variant="outlined"
                className={classes.formField}
                label="Success Message*"
                multiline
                rows="3"
                error={isError('successMessage')}
                {...field}
              />
            )}
            name="successMessage"
            control={control}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <FormControl variant="outlined" className={classes.formField} error={isError('process')}>
            <InputLabel id="process-label">Process*</InputLabel>
            <Controller
              render={({ field }) => (
                <Select
                  labelId="process-label"
                  label="Process"
                  {...field}
                  inputProps={{ readOnly: true }}
                >
                  <MenuItem disabled>Select Process</MenuItem>
                  {Object.keys(InvoiceProcess).map(key => (
                    <MenuItem id={key} value={key} key={key}>
                      {InvoiceProcess[key]}
                    </MenuItem>
                  ))}
                </Select>
              )}
              name="process"
              control={control}
            />
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <Grid container justifyContent="flex-start">
            <Grid item xs={12} sm={12} md={6} lg={6}>
              <FormControlLabel
                control={
                  <Controller
                    render={({ field }) => (
                      <ColorSwitch
                        {...field}
                        id={'supplierReview'}
                        checked={Boolean(field.value)}
                      />
                    )}
                    name={'supplierReview'}
                    control={control}
                  />
                }
                label={'Supplier Review'}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6}>
              <FormControlLabel
                control={
                  <Controller
                    render={({ field }) => (
                      <ColorSwitch {...field} id={'enabled'} checked={Boolean(field.value)} />
                    )}
                    name={'enabled'}
                    control={control}
                  />
                }
                label={'Enabled'}
              />
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6}>
              <FormControlLabel
                control={
                  <Controller
                    render={({ field }) => (
                      <ColorSwitch {...field} id={'autoReject'} checked={Boolean(field.value)} />
                    )}
                    name={'autoReject'}
                    control={control}
                  />
                }
                label={'Auto Reject'}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}></Grid>
        <Grid item xs={12} sm={12} md={12} lg={12} style={{ textAlign: 'center' }}>
          <Button color="primary" type="submit" disabled={isLoading}>
            {isLoading ? 'Saving...' : 'Save'}
          </Button>
        </Grid>
      </Grid>
    </form>
  )
}
